import axios from 'axios';
import { attachAuthInterceptor } from './authInterceptor';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Predictive analytics: forecast, anomalies and reorder suggestions.
 *
 * Super-admin only (ADMIN gets 403). All figures are weight (kg) and counts — there is
 * no monetary series here, and `product_size_id` is never exposed.
 *
 * `skipWarehouseFilter` is set on every request: these endpoints take an explicit
 * `warehouse_ids` argument from the screen, and letting the global SideNav filter be
 * injected on top would silently widen or override that selection.
 */
const intelligenceAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Session-expiry handling (401 -> clear + redirect), consistent with feature services.
attachAuthInterceptor(intelligenceAPI);

intelligenceAPI.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Short-lived in-memory cache, keyed on endpoint + params.
 *
 * These endpoints recompute a multi-week per-product aggregate on every call — the
 * cost is server-side and happens in full before any row is returned. Switching
 * between the three tabs therefore paid for a fresh recompute each time. Caching the
 * response makes tab switching and back-navigation instant; `force: true` (the
 * Refresh button) bypasses it.
 *
 * Memory only, deliberately: this is analytical output that ages, and it should not
 * outlive the page the way a sessionStorage entry would.
 */
const TTL_MS = 5 * 60 * 1000;
const cache = new Map();

const cacheKey = (name, params) => `${name}::${JSON.stringify(params || {})}`;

const readCache = (key) => {
  const hit = cache.get(key);
  if (!hit) return null;
  if (Date.now() > hit.expires) { cache.delete(key); return null; }
  return hit.value;
};

const writeCache = (key, value) => cache.set(key, { value, expires: Date.now() + TTL_MS });

const ok = (data) => ({ success: true, data });
const fail = (error) => ({ success: false, error: extractApiError(error) });

const analyticsIntelligenceService = {
  async getForecast(params = {}, { force = false } = {}) {
    const key = cacheKey('getForecast', params);
    if (!force) {
      const hit = readCache(key);
      if (hit) return ok(hit);
    }
    try {
      const res = await intelligenceAPI.get('/analytics-intelligence/forecast', {
        params, skipWarehouseFilter: true,
      });
      writeCache(key, res.data);
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  async getAnomalies(params = {}, { force = false } = {}) {
    const key = cacheKey('getAnomalies', params);
    if (!force) {
      const hit = readCache(key);
      if (hit) return ok(hit);
    }
    try {
      const res = await intelligenceAPI.get('/analytics-intelligence/anomalies', {
        params, skipWarehouseFilter: true,
      });
      writeCache(key, res.data);
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  /**
   * Advisory only — this never creates a purchase order.
   *
   * The endpoint returns either `{suggestions, needs_review}` OR a bare `[]` when no
   * product moved in-window, so the shape is normalized here and every caller can rely
   * on the object form.
   */
  async getReorderSuggestions(params = {}, { force = false } = {}) {
    const key = cacheKey('getReorderSuggestions', params);
    if (!force) {
      const hit = readCache(key);
      if (hit) return ok(hit);
    }
    try {
      const res = await intelligenceAPI.get('/analytics-intelligence/reorder-suggestions', {
        params, skipWarehouseFilter: true,
      });
      const raw = res.data;
      const normalized = Array.isArray(raw)
        ? { suggestions: raw, needs_review: [], total_suggestions: raw.length, total_needs_review: 0 }
        : {
            suggestions: raw?.suggestions || [],
            needs_review: raw?.needs_review || [],
            // Fall back to the returned length when an older build omits the totals,
            // so the UI never claims more was held back than actually was.
            total_suggestions: raw?.total_suggestions ?? (raw?.suggestions || []).length,
            total_needs_review: raw?.total_needs_review ?? (raw?.needs_review || []).length,
          };
      writeCache(key, normalized);
      return ok(normalized);
    } catch (error) { return fail(error); }
  },
};

export default analyticsIntelligenceService;
